
import React from 'react';
import { ArrowLeft, Loader2, Pause, Play, X, Share, Headset } from 'lucide-react';
import { NewsItem, Language } from '../../types';
import { t } from '../../i18n';

interface NewsDetailHeaderProps {
    newsItem: NewsItem;
    onBack: () => void;
    onShare: () => void;
    lang: Language;
    audioProps: {
        url: string | null;
        isLoading: boolean;
        isReading: boolean;
        hasActivated: boolean;
        currentTime: number;
        duration: number;
        onToggle: () => void;
        onSeek: (time: number) => void;
        onClose: () => void;
    };
}

export const NewsDetailHeader: React.FC<NewsDetailHeaderProps> = ({ newsItem, onBack, onShare, lang, audioProps }) => {
    return (
        <>
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 h-[65px] flex items-center justify-between">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                    <button 
                        onClick={onBack} 
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-50 md:hover:bg-slate-100 text-slate-900 transition-all active:scale-90 shrink-0 border border-slate-100"
                    >
                        <ArrowLeft size={20} strokeWidth={3} />
                    </button>
                    <div className="flex flex-col min-w-0 pr-4">
                        <h2 className={`text-[10px] font-black ${newsItem.source?.toLowerCase() === 'webtoon' ? 'text-[#00d564]' : 'text-rose-500'} uppercase tracking-[0.2em] leading-none mb-1`}>
                            {newsItem.type ? t(lang, newsItem.type) : t(lang, 'news')}
                        </h2>
                        <span className="text-sm font-bold text-slate-900 truncate leading-tight tracking-tight uppercase">
                            {newsItem.title}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {audioProps.url && (
                        <button 
                            onClick={audioProps.onToggle}
                            disabled={audioProps.isLoading}
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-95 border ${
                                audioProps.isReading 
                                ? newsItem.source?.toLowerCase() === 'webtoon' 
                                    ? 'bg-[#00d564] text-white border-[#00d564] shadow-lg shadow-green-100'
                                    : 'bg-rose-500 text-white border-rose-400 shadow-lg shadow-rose-100' 
                                : 'bg-white text-slate-900 border-slate-100 md:hover:bg-slate-50'
                            }`}
                        >
                            {audioProps.isLoading ? (
                                <Loader2 size={18} className="animate-spin" />
                            ) : audioProps.isReading ? (
                                <Pause size={18} fill="currentColor" />
                            ) : (
                                <Play size={18} fill="currentColor" />
                            )}
                        </button>
                    )}
                    
                    <button 
                        onClick={onShare}
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-white text-slate-400 md:hover:text-slate-900 border border-slate-100 transition-all active:scale-90"
                    >
                        <Share size={18} strokeWidth={2.5} />
                    </button>
                </div>
            </header>

            {audioProps.hasActivated && audioProps.url && (
                <div className={`sticky top-[65px] left-0 right-0 z-30 border-b ${newsItem.source?.toLowerCase() === 'webtoon' ? 'border-green-100 bg-green-50/80' : 'border-rose-100 bg-rose-50/80'} backdrop-blur-md`}>
                    <div className="max-w-[700px] mx-auto h-[40px] flex items-center px-4 gap-4">
                        <div className="flex-1 min-w-0 h-4 flex items-center">
                            <input
                                type="range"
                                min="0"
                                max={audioProps.duration || 0}
                                value={audioProps.currentTime}
                                step="0.1"
                                onChange={(e) => audioProps.onSeek(parseFloat(e.target.value))}
                                className={`w-full h-1 ${newsItem.source?.toLowerCase() === 'webtoon' ? 'bg-green-200 accent-[#00d564]' : 'bg-rose-200 accent-rose-500'} rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full ${newsItem.source?.toLowerCase() === 'webtoon' ? '[&::-webkit-slider-thumb]:bg-green-600' : '[&::-webkit-slider-thumb]:bg-rose-600'} [&::-webkit-slider-thumb]:shadow-md md:hover:[&::-webkit-slider-thumb]:scale-125 transition-all`}
                            />
                        </div>
                        <span className={`text-[9px] font-black ${newsItem.source?.toLowerCase() === 'webtoon' ? 'text-green-600' : 'text-rose-600'} uppercase tracking-widest tabular-nums shrink-0`}>
                            {Math.floor(audioProps.currentTime / 60)}:{(audioProps.currentTime % 60).toFixed(0).padStart(2, '0')} / {Math.floor(audioProps.duration / 60)}:{(audioProps.duration % 60).toFixed(0).padStart(2, '0')}
                        </span>
                        <button 
                            onClick={audioProps.onClose} 
                            className={`${newsItem.source?.toLowerCase() === 'webtoon' ? 'text-green-300 md:hover:text-green-600' : 'text-rose-300 md:hover:text-rose-600'} p-1 transition-colors`}
                        >
                            <X size={14} strokeWidth={3} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};
